// Cross-view context (GY-035): project, snapshot, selected symbol, filters,
// graph mode, and impact scenario all live in the URL so browser back/reload and
// copied links restore the same authorized, meaningful view. Values are encoded
// with short keys; defaults are omitted to keep links compact.

import type { Direction, ExplorerMode, FindingStatus, Provenance, Severity } from "../api/types";

export interface ViewState {
  tab: "overview" | "map" | "explore" | "tools" | "contributors" | "wiki" | "reviews";
  snapshotId: string | null; // the snapshot this view was pinned to (stale check)
  seed: string; // explorer origin / cross-view selected symbol
  selected: string | null; // map selection
  // explorer
  mode: ExplorerMode;
  depth: number;
  direction: Direction;
  provenance: Provenance | "all";
  community: string;
  search: string;
  target: string;
  undirected: boolean;
  view: "graph" | "list";
  // map
  mapSeverity: Severity | "all";
  mapStatus: FindingStatus | "all";
  mapSearch: string;
  affected: boolean;
  // impact scenario (GY-040)
  scenarioId: string | null;
}

export const DEFAULT: ViewState = {
  tab: "overview",
  snapshotId: null,
  seed: "",
  selected: null,
  mode: "neighborhood",
  depth: 1,
  direction: "both",
  provenance: "all",
  community: "all",
  search: "",
  target: "",
  undirected: false,
  view: "graph",
  mapSeverity: "all",
  mapStatus: "all",
  mapSearch: "",
  affected: false,
  scenarioId: null,
};

const KEYS: Record<string, keyof ViewState> = {
  t: "tab",
  snap: "snapshotId",
  s: "seed",
  sel: "selected",
  m: "mode",
  d: "depth",
  dir: "direction",
  prov: "provenance",
  c: "community",
  q: "search",
  tg: "target",
  u: "undirected",
  v: "view",
  msev: "mapSeverity",
  mst: "mapStatus",
  mq: "mapSearch",
  aff: "affected",
  scn: "scenarioId",
};

export function decode(search: string): ViewState {
  const p = new URLSearchParams(search);
  const s: ViewState = { ...DEFAULT };
  for (const [short, field] of Object.entries(KEYS)) {
    const raw = p.get(short);
    if (raw === null) continue;
    switch (field) {
      case "depth":
        s.depth = Math.min(4, Math.max(1, Number(raw) || 1));
        break;
      case "undirected":
      case "affected":
        (s[field] as boolean) = raw === "1";
        break;
      default:
        // string-typed fields (validated against unions at use sites)
        (s as unknown as Record<string, unknown>)[field] = raw;
    }
  }
  return s;
}

export function encode(s: ViewState): string {
  const p = new URLSearchParams();
  for (const [short, field] of Object.entries(KEYS)) {
    const value = s[field];
    const def = DEFAULT[field];
    if (value === def || value === null || value === "") continue;
    if (typeof value === "boolean") p.set(short, value ? "1" : "0");
    else p.set(short, String(value));
  }
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}
